import ApiService from './api.service';

export interface IncludedNode {
  pipeline_node_id: string;
  included_field_ids: string[];
}

export interface Template {
  id: string;
  name: string;
  type: 'DATA_MODEL' | 'DATA_SOURCE' | 'REPORTING';
  data_model_template_id?: string;
  included_nodes: IncludedNode[];
  include_relationship_ids: string[];
  include_measure_ids: string[];
  include_dimension_ids: string[];
  template_data: Record<string, any>;
  version: number;
  taxonomic_id?: string;
  description?: string;
  root_account_id?: string;
}

export interface TemplateInstallation {
  id: string;
  template_id: string;
  account_id: string;
  latest_version: number;
  template: Template;
}

export interface InstallationPlan {
  at_next_version: number;
  below_next_version: number;
  no_version: number;
  total: number;
}

export interface TemplateUpdateCheck {
  template_id: string;
  template_name: string;
  installed_version: number;
  latest_version: number;
  has_update: boolean;
}

export class TemplateService {
  private static instance: TemplateService;

  public static getInstance(): TemplateService {
    if (!TemplateService.instance) {
      TemplateService.instance = new TemplateService();
    }
    return TemplateService.instance;
  }

  async findAll(): Promise<Template[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/templates/all'
    ) as { records: Template[] };
    return result.records;
  }

  async findMine(): Promise<Template[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/templates/mine'
    ) as { records: Template[] };
    return result.records;
  }

  async getInstallations(): Promise<TemplateInstallation[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/templates/installations'
    ) as { records: TemplateInstallation[] };
    return result.records;
  }

  async checkForUpdates(): Promise<TemplateUpdateCheck[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/templates/check-for-updates'
    ) as { records: TemplateUpdateCheck[] };
    return result.records;
  }

  async exportTemplate(data: Record<string, any>, justSaveTaxonomicIds?: boolean): Promise<Template | null> {
    const url = justSaveTaxonomicIds
      ? '/templates/export?just_save_taxonomic_ids=true'
      : '/templates/export';
    const result = await ApiService.getInstance().request(
      'POST',
      url,
      data
    ) as { record?: Template; saved?: boolean };
    return result.record || null;
  }

  async updateTemplate(id: string, data: Record<string, any>, justSaveTaxonomicIds?: boolean): Promise<Template | null> {
    const url = justSaveTaxonomicIds
      ? `/templates/update/${id}?just_save_taxonomic_ids=true`
      : `/templates/update/${id}`;
    const result = await ApiService.getInstance().request(
      'PUT',
      url,
      data
    ) as { record?: Template; saved?: boolean };
    return result.record || null;
  }

  async installTemplate(id: string): Promise<any> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/templates/install/${id}`
    );
    return result;
  }

  async upgradeInstallation(installationId: string): Promise<any> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/templates/upgrade/${installationId}`,
      {}
    );
    return result;
  }

  async uninstallTemplate(id: string): Promise<any> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/templates/uninstall/${id}`
    );
    return result;
  }

  async getUpgradeAllPlan(id: string): Promise<InstallationPlan> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/templates/upgrade-all-installations/${id}/plan`
    ) as { record: InstallationPlan };
    return result.record;
  }

  async upgradeAllInstallations(id: string, opts?: { publish_to_production?: boolean }): Promise<{ job_id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/templates/upgrade-all-installations/${id}`,
      opts
    ) as { job_id: string };
    return result;
  }
}

export const templateService = TemplateService.getInstance();
